import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { getAccessibleCustomAppNames } from "#src/view-models/custom-apps";
import { useQuery } from "@tanstack/react-query";
import {
  AppRegistryAdapter,
  AppRegistryAdapterQueryKeys,
} from "@validereinc/domain";
import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

type CustomAppRegistryContextType = {
  /** the names, as the app registry knows them, of the custom apps the current user can access. Empty until loaded and whenever the request fails. */
  accessibleCustomAppNames: string[];
  isLoading: boolean;
};

// a stable reference so consumers that memoize on the list don't recompute on every render
const NO_ACCESSIBLE_CUSTOM_APP_NAMES: string[] = [];

export const CustomAppRegistryContext =
  createContext<CustomAppRegistryContextType>({
    accessibleCustomAppNames: NO_ACCESSIBLE_CUSTOM_APP_NAMES,
    isLoading: false,
  });

/**
 * Loads which custom apps the current user can access. Only users in the v2
 * auth system are asked, since the registry lives in Carbon Hub. The request
 * is made once per session and again when the company changes. A failed or
 * empty request simply means no custom apps are available.
 */
export const CustomAppRegistryProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const {
    isAuthenticated,
    activeAuthVersion,
    claims: { company },
  } = useAuthenticatedContext();
  const isV2AuthActive =
    activeAuthVersion !== "unknown" && activeAuthVersion.includes("v2");

  const {
    data: accessibleCustomAppNames = NO_ACCESSIBLE_CUSTOM_APP_NAMES,
    isInitialLoading: isLoadingAppRegistry,
  } = useQuery({
    queryKey: [
      ...AppRegistryAdapterQueryKeys.listAccessibleCustomApps(),
      company.id,
    ],
    queryFn: () => AppRegistryAdapter.listAccessibleCustomApps(),
    select: getAccessibleCustomAppNames,
    enabled: isAuthenticated && isV2AuthActive,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    // the entry is a convenience, so a failure must not surface an error toast
    meta: { hideErrorToasts: true },
  });

  const contextValue = useMemo(
    () => ({
      accessibleCustomAppNames,
      isLoading: isLoadingAppRegistry,
    }),
    [accessibleCustomAppNames, isLoadingAppRegistry]
  );

  return (
    <CustomAppRegistryContext.Provider value={contextValue}>
      {children}
    </CustomAppRegistryContext.Provider>
  );
};

/** Which custom apps the current user can access. Empty when there is no provider above. */
export const useCustomAppRegistry = () => useContext(CustomAppRegistryContext);
